import React, { useState } from 'react';
import { Eyebrow } from '../components/xc/Bits';
import { useDemo } from '../context/DemoContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { Switch } from '../components/ui/switch';
import { ShieldCheck, Wallet, TrendingDown, Clock, Bell, Timer, UserX, Info, Check } from 'lucide-react';

function ControlRow({ icon: Icon, title, value, children }) {
  return (
    <div className="xc-glass rounded-2xl p-5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="h-11 w-11 rounded-xl grid place-items-center flex-shrink-0" style={{ background: '#2FBF7116', border: '1px solid #2FBF7140' }}><Icon className="h-5 w-5 text-[#2FBF71]" /></div>
        <div><div className="font-500 text-white">{title}</div>{value && <div className="text-sm text-white/50 mt-0.5">{value}</div>}</div>
      </div>
      {children}
    </div>
  );
}

const AVAIL = [
  { id: 'available', label: 'Available for Missions', color: '#2FBF71' },
  { id: 'casino', label: 'Casino Only', color: '#B026FF' },
  { id: 'social', label: 'Social / Exploring', color: '#E0B85A' },
  { id: 'dnd', label: 'Do Not Disturb', color: '#FF204E' },
];

export default function SafePlay() {
  const { toast } = useToast();
  const { playerMode, setPlayerMode } = useDemo();
  const [reality, setReality] = useState(true);
  const avail = playerMode;
  const setAvail = setPlayerMode;
  const edit = (what) => toast({ title: 'Demo control', description: `${what} would open an editor in production.` });

  return (
    <div className="bg-[#070609] min-h-screen">
      <Toaster />
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(80% 100% at 20% 0%, rgba(47,191,113,0.12), transparent 60%), #0D0B10' }} />
        <div className="relative max-w-[1080px] mx-auto px-4 lg:px-8 py-14">
          <Eyebrow color="#2FBF71">Responsible Gambling</Eyebrow>
          <h1 className="font-display font-700 text-4xl md:text-5xl text-white tracking-tight">YOU'RE IN <span className="text-[#2FBF71]">CONTROL</span></h1>
          <p className="mt-3 text-white/60 max-w-xl">Set your own limits. These controls always override Xincity mechanics — no mission can ever change them.</p>
        </div>
      </section>

      <section className="max-w-[1080px] mx-auto px-4 lg:px-8 py-8 space-y-4">
        <ControlRow icon={Wallet} title="Deposit Limit" value="€500 / week"><button onClick={() => edit('Deposit limit')} className="btn-ghost rounded-lg px-4 py-2 text-sm">Edit</button></ControlRow>
        <ControlRow icon={TrendingDown} title="Loss Limit" value="€300 / week"><button onClick={() => edit('Loss limit')} className="btn-ghost rounded-lg px-4 py-2 text-sm">Edit</button></ControlRow>
        <ControlRow icon={Bell} title="Session Reminder" value="Every 60 minutes"><button onClick={() => edit('Session reminder')} className="btn-ghost rounded-lg px-4 py-2 text-sm">Edit</button></ControlRow>
        <ControlRow icon={Clock} title="Reality Check" value={reality ? 'Enabled' : 'Disabled'}><Switch checked={reality} onCheckedChange={(v) => { setReality(v); toast({ title: `Reality check ${v ? 'enabled' : 'disabled'}` }); }} /></ControlRow>
        <ControlRow icon={Timer} title="Session Duration" value="Current session: 42 minutes"><span className="text-xs text-white/40">live</span></ControlRow>
        <ControlRow icon={UserX} title="Self-Exclusion" value="Take a break for a set period"><button onClick={() => edit('Self-exclusion')} className="btn-ghost rounded-lg px-4 py-2 text-sm">Manage</button></ControlRow>

        {/* Mission availability */}
        <div className="xc-glass rounded-2xl p-6">
          <div className="font-display font-700 text-white mb-1">Mission Availability</div>
          <p className="text-sm text-white/50 mb-4">Control how visible you are to crews. This is yours alone — private compliance information is never disclosed.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {AVAIL.map((a) => (
              <button key={a.id} onClick={() => setAvail(a.id)} className={`rounded-xl p-4 border flex items-center justify-between text-left ${avail === a.id ? 'bg-white/[0.05]' : 'bg-white/[0.02] hover:bg-white/[0.04]'}`} style={{ borderColor: avail === a.id ? a.color : 'rgba(255,255,255,0.08)' }}>
                <span className="flex items-center gap-2.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: a.color, boxShadow: `0 0 8px ${a.color}` }} /><span className="text-sm text-white/85">{a.label}</span></span>
                {avail === a.id && <Check className="h-4 w-4" style={{ color: a.color }} />}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl p-6 border border-[#2FBF71]/25 bg-[#2FBF71]/6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-[#2FBF71] flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-sm text-white/70 leading-relaxed">Responsible-gambling controls always override Xincity missions. If a player becomes unavailable, other players simply see “Crew Slot Available.” Private compliance information is never disclosed.</p>
              <p className="text-sm text-white/70 leading-relaxed">Leaving or declining a mission never damages player or crew standing.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
